import { useEffect, useRef, useState } from "react"
import withRouter from "Components/Common/withRouter"
import ActionButton from "Components/atoms/buttons/action-button"
import LoadingState from "Components/organisms/loading-state"
import MarkdownViewer from "Components/molecule/markdown-viewer"
import { getManual } from "slices/manuals/thunk"
import { Manual } from "models/manual"
import logo from "assets/images/logo-dark.png"

// Screenshots load asynchronously; give them this long before printing anyway
const IMAGE_WAIT_MS = 10000

/** Resolves once every manual image has finished loading (or the wait runs out) */
const waitForImages = async (root: HTMLElement) => {
    const deadline = Date.now() + IMAGE_WAIT_MS
    while (Date.now() < deadline) {
        const pending = root.querySelector(".markdown-viewer__img-placeholder")
        const images = Array.from(root.querySelectorAll("img"))
        if (!pending && images.every(img => img.complete)) return
        await new Promise(resolve => setTimeout(resolve, 200))
    }
}

// Route: /manuals/:module/:name/print (no app layout). Opens the print dialog once loaded.
const ManualPrintPage = (props: any) => {
    const { module, name } = props.router.params
    const slug = module && name ? `${module}/${name}` : ""
    const [manual, setManual] = useState<Manual | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const contentRef = useRef<HTMLDivElement>(null)

    document.title = `${manual?.title ?? "Manual"} | IbexIQ`

    useEffect(() => {
        const load = async () => {
            setManual(slug ? await getManual(slug) : null)
            setIsLoading(false)
        }
        load()
    }, [slug])

    useEffect(() => {
        if (!manual || !contentRef.current) return
        let cancelled = false
        waitForImages(contentRef.current).then(() => {
            if (!cancelled) window.print()
        })
        return () => { cancelled = true }
    }, [manual])

    if (isLoading) return <LoadingState title="Loading Manual" subtitle="Preparing document…" />
    if (!manual) return <p className="p-4 text-muted">This manual is not available.</p>

    return (
        <div className="manual-print">
            <div className="manual-print__toolbar">
                <ActionButton onClick={() => window.print()}>Print / Save as PDF</ActionButton>
                <ActionButton onClick={() => window.close()}>Close</ActionButton>
            </div>
            <div className="manual-print__page" ref={contentRef}>
                <header className="manual-print__header">
                    <img src={logo} alt="IbexIQ" className="manual-print__logo" />
                    <div>
                        <h1>{manual.title}</h1>
                        <p>
                            {manual.module} manual
                            {manual.lastUpdated && <> · Updated {manual.lastUpdated}</>}
                        </p>
                    </div>
                </header>
                <MarkdownViewer content={manual.content} />
            </div>
        </div>
    )
}

export default withRouter(ManualPrintPage)
